import React, { useContext, useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { GlobalContext } from '../GlobalContext'
import { subscribe } from '../utils/pubsub'
import Spinner from './Spinner'
import YellowAlert from './YellowAlert'
const mapTilerAPIKey = '1j1RqlmNhygo7wmzR2gu'



type Props = {
  locationFromGps: Coordinates | null
  locationFromUser: Coordinates | null
  locationAsString: string
  locationManuallyEntered?: string
  setLocationFromGps: React.Dispatch<React.SetStateAction<Coordinates | null>>
  setLocationFromUser: React.Dispatch<React.SetStateAction<Coordinates | null>>
  setLocationAsString: React.Dispatch<React.SetStateAction<string>>
  setLocationManuallyEntered: React.Dispatch<React.SetStateAction<any>>
}

export default function LocationMapVector({
  locationFromGps,
  locationFromUser,
  locationAsString,
  locationManuallyEntered,
  setLocationFromGps,
  setLocationFromUser,
  setLocationAsString,
  setLocationManuallyEntered
}: Props) {
  const mapContainerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  const emojiMarkerRef = useRef<maplibregl.Marker | null>(null)
  const gpsMarkerRef = useRef<maplibregl.Marker | null>(null)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [personEmoji, setPersonEmoji] = useState('🧍‍♀️')
  const [mapLoadError, setMapLoadError] = useState<string | null>(null)
  const { showModal, setShowModal, sendMessageToWebsocket } = useContext(GlobalContext)




  // init map
  useEffect(() => {


    if (mapRef.current || !mapContainerRef.current) {
      if (mapRef.current) console.log('Map already initialized')
      if (!mapContainerRef.current) console.log('No map container ref')
      return
    }

    let center = [0, 0] as maplibregl.LngLatLike
    if (locationFromGps) {
      center = [locationFromGps.lng, locationFromGps.lat]
    }

    console.log("starting maplibrelg map in element:", mapContainerRef.current)
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: `https://api.maptiler.com/maps/streets-v2/style.json?key=${mapTilerAPIKey}`, // works
      center: center,
      zoom: 13
    })

    mapRef.current = map


    // emoji marker element
    const emojiEl = document.createElement('div')
    emojiEl.style.fontSize = '30px'
    emojiEl.style.width = '40px'
    emojiEl.style.height = '40px'
    emojiEl.style.textAlign = 'center'
    emojiEl.style.lineHeight = '40px'
    emojiEl.style.borderRadius = '50%'
    emojiEl.style.background = 'rgba(255,255,255,0.85)'
    emojiEl.style.boxShadow = '0 0 4px rgba(0,0,0,0.4)'
    emojiEl.innerText = personEmoji

    // center (emoji) marker — stays at map center
    emojiMarkerRef.current = new maplibregl.Marker({ element: emojiEl })
      .setLngLat([map.getCenter().lng, map.getCenter().lat])
      .addTo(map)

    // GPS marker — follows `location`
    const gpsEl = document.createElement('div')
    gpsEl.style.width = '14px'
    gpsEl.style.height = '14px'
    gpsEl.style.borderRadius = '50%'
    gpsEl.style.background = '#0d6efd'
    gpsEl.style.boxShadow = '0 0 6px rgba(0,0,0,0.5)'
    // gpsMarkerRef.current = new maplibregl.Marker({ element: gpsEl })
    //   .setLngLat([locationFromGps!.lng, locationFromGps!.lat])
    //   .addTo(map)

    // movement handlers
    map.on('move', (e: any) => {
      console.log('Map move....')
      const c = map.getCenter()
      setPersonEmoji('🚶‍♂️')
      // keep emoji marker on center
      emojiMarkerRef.current?.setLngLat([c.lng, c.lat])
    })

    map.on('moveend', (e: any) => {
      const c = map.getCenter()
      const isUserEvent = e.originalEvent ? true : false
      if (c.lat === 0 && c.lng === 0) return
      if (!isUserEvent) return // ignore programmatic moves
      console.log('Map moveend, new center:', c)
      console.log(e)
      setLocationFromUser({ lat: c.lat, lng: c.lng })
      setPersonEmoji('🧍‍♀️')
      emojiMarkerRef.current?.setLngLat([c.lng, c.lat])
    })

    // basic visibility/logging
    map.on('load', () => {
      console.log('🌍 map loaded')
      setMapLoaded(true)
    })
    map.on('error', (e: any) => {
      console.error('map error', e)
      setMapLoadError('Map failed to load')
    })
    map.on('styledataloading', e => console.log('styledataloading', e.dataType));
    map.on('styledata', e => console.log('styledata', e.dataType));
    //@ts-ignore
    map.on('dataloading', e => console.log('dataloading', e.dataType, e.sourceId));
    //@ts-ignore
    map.on('data', e => console.log('data', e.dataType, e.sourceId, e.isSourceLoaded));
    map.on('render', () => console.log('render tick'));
    map.on('idle', () => console.log('idle'));
    window.addEventListener('error', e => console.error('window error', e.error || e));
    window.addEventListener('unhandledrejection', e => console.error('unhandledrejection', e.reason));

    return () => {
      map.remove()
      mapRef.current = null
      emojiMarkerRef.current = null
      gpsMarkerRef.current = null
    }
  }, []) // init once StrictMode-safe due to cleanup nulling refs


  const getCurrentMapCenter = () => {
    const map = mapRef.current
    if (!map) return null
    const center = map.getCenter()
    return { oldLat: center.lat, oldLng: center.lng }
  }

  // update GPS marker position when `location` changes
  useEffect(() => {
    if (locationFromGps === null) return
    const { oldLat, oldLng } = getCurrentMapCenter() || { oldLat: 0, oldLng: 0 }
    // look for changes to avoid minor jitter
    const threshold = 0.01 // degrees (~1.1km)
    const latChange = Math.abs(oldLat - locationFromGps!.lat)
    const lngChange = Math.abs(oldLng - locationFromGps!.lng)
    const bigChange = latChange > threshold || lngChange > threshold

    gpsMarkerRef.current?.setLngLat([locationFromGps!.lng, locationFromGps!.lat])
    // recenter (triggers your move/moveend handlers, which keep the emoji on center)
    if (bigChange) {
      mapRef.current?.easeTo({
        center: [locationFromGps!.lng, locationFromGps!.lat],
        duration: 300, // tweak or use flyTo if you prefer
      })
    }
  }, [locationFromGps])




  // update emoji content when it changes
  useEffect(() => {
    const el = emojiMarkerRef.current?.getElement()
    if (el) el.textContent = personEmoji
  }, [personEmoji])

  useEffect(() => {
    if (!navigator.geolocation) return console.error('Geolocation not supported')
    const watchId = navigator.geolocation.watchPosition(
      (pos) => setLocationFromGps({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        const error_message = err.message || 'Geolocation error'
        setMapLoadError(error_message)
        console.error('Geolocation error', err)
      }
    )
    return () => navigator.geolocation.clearWatch(watchId)
  }, [])

  useEffect(() => {
    const coords = locationFromUser || locationFromGps
    if (mapLoaded && coords && coords.lat !== 0 && coords.lng !== 0) {
      fetchAddressForCoordinates(coords)
    }
  }, [mapLoaded, locationFromGps, locationFromUser])


  const fetchAddressForCoordinates = (location: Coordinates) => {
    console.log('🤖 Fetching address for coordinates:', location)
    const unsubscribe = subscribe('addressFoundForCoordinates', (data: any) => {
      // console.log(data)
      if (data) {
        setLocationAsString(data)
      }
      unsubscribe()
    })
    const payload = {
      action: "checklistsClientApp",
      subAction: 'findAddressForCoordinates',
      location
    }
    sendMessageToWebsocket(JSON.stringify(payload))
  }



  let latFromUser = locationFromUser ? locationFromUser.lat : null
  let lngFromUser = locationFromUser ? locationFromUser.lng : null
  let latFromGps = locationFromGps ? locationFromGps.lat : null
  let lngFromGps = locationFromGps ? locationFromGps.lng : null
  let lat = latFromUser || latFromGps || null
  let lng = lngFromUser || lngFromGps || null
  let latStr = lat ? parseFloat(lat as any).toFixed(5) : ''
  let lngStr = lng ? parseFloat(lng as any).toFixed(5) : ''
  const coordinates = latStr && lngStr ? `Latitude: ${latStr}, Longitude: ${lngStr}` : null




  if (mapLoadError) {
    return <div className={`w-full flex flex-col gap-6`}>

      <YellowAlert icon={true} size='large' alignment='left'>
        <div className={`w-full flex flex-col`}>
          <h2 className="text-lg font-bold">We could not automatically detect your location</h2>
          <p>Please ensure location services are enabled in your browser. Error type: {mapLoadError}</p>
        </div>
      </YellowAlert>

      <div className={`w-full flex flex-col gap-4`}>
        <p className={`text-3xl font-righteous text-brandblue`}>Manually enter location description:</p>
        <input type='text'
          className={` text-center text-lg px-5 py-3 bg-white drop-shadow-lg border border-gray-300 text-gray-900 rounded hover:opacity-90-lg focus:ring-blue-500 focus:border-blue-500 block w-full`}
          value={locationManuallyEntered}
          onChange={(e) => setLocationManuallyEntered(e.target.value)}
        />
      </div>
    </div>
  }


  return <div className="fixed top-0 left-0 h-screen w-screen">


    <div ref={mapContainerRef} className="w-screen h-screen" style={{ zIndex: 0 }} />


    <div className='fixed w-full' style={{
      zIndex: 50,
      bottom: '70px',
      margin: '0 auto'
    }}>

      <div className='w-full flex flex-col items-center justify-center px-6'>
        <div className='w-full max-w-2xl bg-white/70 rounded p-2'>
          {!locationFromGps && <Spinner size='large'>Getting location...</Spinner>}
          {locationAsString && <p className={`font-bold`}>{locationAsString}</p>}
          {coordinates && <p className={`text-xs`}>{coordinates}</p>}
        </div>
      </div>

    </div>



  </div >
}
